import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import VaultTree, { VaultTreeNode } from '../components/VaultTree';
import KnowledgeNoteView, { KnowledgeNote } from '../components/KnowledgeNoteView';
import NewNoteModal from '../components/NewNoteModal';
import { API_BASE_URL, authFetch } from '../config/api';

// Excluded from the Recent strip — BRC v9 clauses were bulk-imported as a
// reference archive and would otherwise dominate the list.
const RECENT_EXCLUDE_PREFIX = '2 Areas/Food Safety/QMS/BRC v9/';

interface TreeResponse {
  ok: boolean;
  root: string;
  tree: VaultTreeNode[];
}

interface NoteResponse {
  ok: boolean;
  note: KnowledgeNote;
}

interface RecentEntry {
  path: string;
  title: string;
  mtime: string; // ISO
}

interface RecentResponse {
  ok: boolean;
  recent: RecentEntry[];
  excluded: string | null;
}

async function fetchTree(): Promise<TreeResponse> {
  const res = await authFetch(`${API_BASE_URL}/api/knowledge/vault/tree`, { cache: 'no-store' });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

async function fetchNote(path: string): Promise<KnowledgeNote> {
  const res = await authFetch(`${API_BASE_URL}/api/knowledge/notes/${encodeURIComponent(path)}`);
  if (!res.ok) throw new Error(await res.text());
  const data = (await res.json()) as NoteResponse;
  return data.note;
}

async function fetchPendingCount(): Promise<number> {
  const res = await authFetch(`${API_BASE_URL}/api/knowledge/staging`, { cache: 'no-store' });
  if (!res.ok) return 0;
  const data = (await res.json()) as { ok: boolean; proposals: { status: string }[] };
  return data.proposals?.filter((p) => p.status === 'pending').length ?? 0;
}

async function fetchHealthCount(): Promise<number> {
  // Counts-only probe — the full payload is fetched by KnowledgeHealthPage
  // itself. We just need a number for the header badge.
  const res = await authFetch(`${API_BASE_URL}/api/knowledge/health`, { cache: 'no-store' });
  if (!res.ok) return 0;
  const data = (await res.json()) as { ok: boolean; total_issues?: number };
  return data.total_issues ?? 0;
}

async function fetchRecent(): Promise<RecentEntry[]> {
  const qs = new URLSearchParams({
    limit: '10',
    exclude: RECENT_EXCLUDE_PREFIX,
  });
  const res = await authFetch(`${API_BASE_URL}/api/knowledge/vault/recent?${qs.toString()}`, {
    cache: 'no-store',
  });
  if (!res.ok) return [];
  const data = (await res.json()) as RecentResponse;
  return data.recent ?? [];
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(0, Math.floor((now - then) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  const y = Math.floor(d / 365);
  return `${y}y ago`;
}

async function downloadVaultZip(): Promise<void> {
  const res = await authFetch(`${API_BASE_URL}/api/knowledge/vault/download`);
  if (!res.ok) throw new Error(await res.text());
  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = disposition.match(/filename="([^"]+)"/);
  const filename = match ? match[1] : `fsnu-vault-${new Date().toISOString().slice(0, 10)}.zip`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function KnowledgeBrowsePage() {
  // `?path=<relPath>` deep-links from the Health page (and anywhere else
  // that wants to pre-select a specific note). Falls back to the last
  // session-stored selection for organic navigation.
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedPath, setSelectedPath] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const fromUrl = searchParams.get('path');
    if (fromUrl) return fromUrl;
    return sessionStorage.getItem('knowledgeBrowse.selectedPath');
  });
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [newNoteOpen, setNewNoteOpen] = useState(false);
  const queryClient = useQueryClient();

  // Keep in sync if the user navigates here with a different `?path=` later.
  useEffect(() => {
    const fromUrl = searchParams.get('path');
    if (fromUrl && fromUrl !== selectedPath) {
      setSelectedPath(fromUrl);
      try {
        sessionStorage.setItem('knowledgeBrowse.selectedPath', fromUrl);
      } catch {
        /* storage unavailable */
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleDownload = useCallback(async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      await downloadVaultZip();
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setDownloading(false);
    }
  }, []);

  const handleSelect = useCallback((path: string) => {
    setSelectedPath(path);
    try {
      sessionStorage.setItem('knowledgeBrowse.selectedPath', path);
    } catch {
      /* storage unavailable — no-op */
    }
    // Mirror selection into the URL so deep-links, browser back/forward,
    // refresh, and new-tab clicks on in-page links (backlinks, wikilinks,
    // recent strip) all point at the right note. `replace: true` keeps the
    // browser history clean — back button should leave the Knowledge
    // Center, not walk through every note the user opened.
    setSearchParams({ path }, { replace: true });
  }, [setSearchParams]);

  const treeQuery = useQuery<TreeResponse, Error>(['vaultTree'], fetchTree);
  const pendingQuery = useQuery<number, Error>(['stagingPendingCount'], fetchPendingCount, {
    refetchOnWindowFocus: true,
  });
  const healthQuery = useQuery<number, Error>(['knowledgeHealthCount'], fetchHealthCount, {
    staleTime: 5 * 60_000, // 5 min — walk+query is not free, don't refetch on every focus
  });
  const recentQuery = useQuery<RecentEntry[], Error>(['vaultRecent'], fetchRecent, {
    refetchOnWindowFocus: true,
  });
  const noteQuery = useQuery<KnowledgeNote, Error>(
    ['knowledgeNote', selectedPath],
    () => fetchNote(selectedPath as string),
    { enabled: !!selectedPath },
  );

  const handleNoteCreated = useCallback(
    (createdPath: string) => {
      setNewNoteOpen(false);
      handleSelect(createdPath);
      // Re-fetch the tree + recent so the new note shows up immediately.
      queryClient.invalidateQueries(['vaultTree']);
      queryClient.invalidateQueries(['vaultRecent']);
    },
    [handleSelect, queryClient],
  );

  // Build an index of title → file path so wikilinks can resolve by title.
  const titleIndex = useMemo(() => {
    const map = new Map<string, string>();
    if (treeQuery.data) collectTitles(treeQuery.data.tree, map);
    return map;
  }, [treeQuery.data]);

  const totalNotes = useMemo(
    () => (treeQuery.data ? countFiles(treeQuery.data.tree) : 0),
    [treeQuery.data],
  );

  const handleWikilink = useCallback(
    (target: string) => {
      // If target already looks like a vault-relative path (contains a slash
      // and ends in .md), select it directly — used by the Backlinks panel.
      if (target.includes('/') && target.endsWith('.md')) {
        handleSelect(target);
        return;
      }
      // Otherwise resolve by title. Exact match first, then case-insensitive.
      let resolved = titleIndex.get(target);
      if (!resolved) {
        const lower = target.toLowerCase();
        for (const [k, v] of titleIndex.entries()) {
          if (k.toLowerCase() === lower) {
            resolved = v;
            break;
          }
        }
      }
      if (resolved) handleSelect(resolved);
    },
    [titleIndex, handleSelect],
  );

  return (
    <div className="knowledge-browse-page">
      <div className="knowledge-browse-page__header">
        <div>
          <h2 className="knowledge-browse-page__title">Knowledge Center</h2>
          <p className="text-muted">
            Browse published notes from the Obsidian vault ({treeQuery.data?.root ?? '—'}).
          </p>
        </div>
        <div className="knowledge-browse-page__stats" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div className="knowledge-stat">
            <span className="knowledge-stat__value">{totalNotes}</span>
            <span className="knowledge-stat__label">notes</span>
          </div>
          <button
            type="button"
            className="btn btn--approve"
            onClick={() => setNewNoteOpen(true)}
            title="Create a new note from a template"
          >
            + New note
          </button>
          <button
            type="button"
            className="btn"
            onClick={handleDownload}
            disabled={downloading}
            title="Download all Food Safety notes as a .zip for upload to a Claude Project"
          >
            {downloading ? 'Preparing…' : 'Download vault (.zip)'}
          </button>
          <Link to="/knowledge/staging" className="btn">
            Staging
            {pendingQuery.data && pendingQuery.data > 0 ? (
              <span className="knowledge-pending-badge" style={{ marginLeft: 6 }}>
                {pendingQuery.data}
              </span>
            ) : null}
          </Link>
          <Link to="/knowledge/health" className="btn" title="Orphan notes, broken wikilinks, stale content, near-duplicates">
            Health
            {healthQuery.data && healthQuery.data > 0 ? (
              <span className="knowledge-pending-badge" style={{ marginLeft: 6 }}>
                {healthQuery.data}
              </span>
            ) : null}
          </Link>
        </div>
      </div>
      {downloadError && (
        <div className="error-state" style={{ marginTop: 8 }}>
          Download failed: {downloadError}
        </div>
      )}

      {recentQuery.data && recentQuery.data.length > 0 && (
        <RecentStrip
          entries={recentQuery.data}
          selectedPath={selectedPath}
          onSelect={handleSelect}
        />
      )}

      <div className="knowledge-browse-page__layout">
        <aside className="knowledge-browse-page__tree">
          {treeQuery.isLoading && <div className="loading-state">Loading tree...</div>}
          {treeQuery.isError && (
            <div className="error-state">Error: {treeQuery.error.message}</div>
          )}
          {treeQuery.data && (
            <VaultTree
              nodes={treeQuery.data.tree}
              selectedPath={selectedPath}
              onSelect={handleSelect}
            />
          )}
        </aside>

        <main className="knowledge-browse-page__detail">
          {!selectedPath && (
            <EmptyDetail totalNotes={totalNotes} />
          )}
          {selectedPath && noteQuery.isLoading && (
            <div className="loading-state">Loading note...</div>
          )}
          {selectedPath && noteQuery.isError && (
            <div className="error-state">
              <div>Could not load note.</div>
              <div className="text-muted" style={{ marginTop: 8 }}>
                {noteQuery.error.message}
              </div>
            </div>
          )}
          {noteQuery.data && (
            <KnowledgeNoteView
              note={noteQuery.data}
              onWikilinkClick={handleWikilink}
              titleIndex={titleIndex}
            />
          )}
        </main>
      </div>

      {newNoteOpen && (
        <NewNoteModal
          onClose={() => setNewNoteOpen(false)}
          onCreated={handleNoteCreated}
        />
      )}
    </div>
  );
}

// ── Recent-edits strip above the tree ─────────────────────────────────────

function RecentStrip({
  entries,
  selectedPath,
  onSelect,
}: {
  entries: RecentEntry[];
  selectedPath: string | null;
  onSelect: (path: string) => void;
}) {
  return (
    <div className="knowledge-recent-strip" aria-label="Recently edited notes">
      <span className="knowledge-recent-strip__label">Recent</span>
      <div className="knowledge-recent-strip__items">
        {entries.map((e) => {
          const isActive = e.path === selectedPath;
          return (
            <button
              key={e.path}
              type="button"
              className={`knowledge-recent-chip${isActive ? ' knowledge-recent-chip--active' : ''}`}
              onClick={() => onSelect(e.path)}
              title={`${e.path} · ${new Date(e.mtime).toLocaleString()}`}
            >
              <span className="knowledge-recent-chip__title">{e.title}</span>
              <span className="knowledge-recent-chip__time">{relativeTime(e.mtime)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Empty state shown when no note is selected ────────────────────────────

function EmptyDetail({ totalNotes }: { totalNotes: number }) {
  return (
    <div className="knowledge-empty-detail knowledge-empty-detail--rich">
      <div className="knowledge-empty-detail__icon" aria-hidden>{'\u{1F4D6}'}</div>
      <h3 className="knowledge-empty-detail__title">Select a note to begin</h3>
      <p className="knowledge-empty-detail__hint">
        {totalNotes > 0
          ? `Pick any of the ${totalNotes} notes from the tree on the left.`
          : 'No notes indexed yet — once the vault syncs, notes will appear here.'}
      </p>
      <ul className="knowledge-empty-detail__tips">
        <li>Use the search box to filter by note title or path.</li>
        <li>Folders collapse and remember their state while you browse.</li>
        <li>Click a <code>[[wikilink]]</code> inside any note to jump to it.</li>
      </ul>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────

function collectTitles(nodes: VaultTreeNode[], out: Map<string, string>): void {
  for (const n of nodes) {
    if (n.type === 'file') {
      // Keep the first occurrence; the tree is sorted folder-then-file so this is stable.
      if (!out.has(n.title)) out.set(n.title, n.path);
    } else {
      collectTitles(n.children, out);
    }
  }
}

function countFiles(nodes: VaultTreeNode[]): number {
  let n = 0;
  for (const node of nodes) {
    if (node.type === 'file') n += 1;
    else n += countFiles(node.children);
  }
  return n;
}
